import { createSelector } from "reselect";
import { createDBServiceSlice } from "./db-service-reducers";
import moment from "moment";
import { ReportRecord } from "../interfaces/report-record";
import { RootState } from "./store";
import { trafficReportSelectByQuery } from "./traffic-reports-service-reducer";
import { AffiliateNetwork, Campaign, TrafficReportRecord } from "./api-interfaces";
import { selectUnifiedCampaignList, selectUnifiedAffiliateNetworkList } from './unified-selectors';
import { DBPaymentsReducer, paymentsSelectByQuery } from "./payments-service-reducer";

// report Reducer
export const {
    slice: ReportSlice,
    dbThunks: _ReportThunks,
    selectByQuery: _reportSelectByQuery,
    selectOneObjectByQuery: _reportSelectOneObjectByQuery,
    selectOneFieldByQuery: _reportSelectOneFieldByQuery,
    selectors: _reportSelectors,
    selectByQueryOnlyLength: _reportSelectByQueryOnlyLength,
    selectByQueryOnlyMaxMin: _reportSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<ReportRecord>({
    service: "report",
    initialState: { ids: [], entities: {}, itemsLoaded: false, itemsLoading: false },
    getReducerState: (state) => state.DBReportReducer,
});
export const DBReportReducer = ReportSlice.reducer;
export const ReportReducerActions = ReportSlice.actions;
export type reportReducerActions = keyof typeof ReportReducerActions;
export const DBReportThunks = _ReportThunks;
export const reportSelectors = _reportSelectors;
export const reportSelectByQuery = _reportSelectByQuery;
export const reportSelectByQueryOnlyLength = _reportSelectByQueryOnlyLength;
export const reportSelectByQueryOnlyMaxMin = _reportSelectByQueryOnlyMaxMin;
export const reportSelectOneObjectByQuery = _reportSelectOneObjectByQuery;
export const reportSelectOneFieldByQuery = _reportSelectOneFieldByQuery;

export interface DateRange {
    startDate: Date | null;
    endDate: Date | null;
}
export const selectReportsByDateRange = createSelector(
    [
        reportSelectors.selectAll,
        (state: any, dateRange: DateRange) => dateRange
    ],
    (reports, dateRange: DateRange) => {
        const filteredReports = reports
            .map(report => {
                return {
                    ...report,
                    revenue: parseFloat(report.revenue?.toFixed(2) ?? 0),
                    date: moment(report.date),
                }
            })
            .filter(report => report.channelTag)
            .filter(report => report.date.isBetween(moment(dateRange?.startDate), moment(dateRange?.endDate), 'day', '[]'))

        filteredReports.sort((a, b) => b.date.diff(a.date))

        return filteredReports;
    }
)

export const selectReports = createSelector(
    reportSelectors.selectAll,
    (reports) => {
        reports = reports
            .map(report => {
                return {
                    ...report,
                    revenue: parseFloat(report.revenue.toFixed(2)),
                    date: moment(report.date),
                }
            })
        reports.sort((a, b) => b.date.diff(a.date))

        return reports;
    }
)


// select report by query aggregated per day
export const selectReportByQueryAggregatedPerDayAndFeedTag = createSelector(
    [
        (state: RootState, query: any) => reportSelectByQuery(state.DBReportReducer, query),
        (state: RootState, query: any) => trafficReportSelectByQuery(state.DBTrafficReportReducer, query),
    ],
    (reports, trafficReports): { date: string, feedTagData: { [feedTag: string]: ReportRecord[] } }[] => {
        const aggregatedReportsByDate: { [date: string]: { feedTagData: { [feedTag: string]: ReportRecord[] } } } = {};

        reports.forEach((report: ReportRecord) => {
            const dateKey = moment.utc(report.date).format('DD-MM-YYYY');
            const feedTag = report.feedTag;

            if (!aggregatedReportsByDate[dateKey]) {
                aggregatedReportsByDate[dateKey] = { feedTagData: {} };
            }

            if (!aggregatedReportsByDate[dateKey].feedTagData[feedTag]) {
                aggregatedReportsByDate[dateKey].feedTagData[feedTag] = [];
            }

            const existingReport = aggregatedReportsByDate[dateKey].feedTagData[feedTag].find(
                (r) => moment.utc(r.date).format('DD-MM-YYYY') === dateKey
            );

            if (existingReport) {
                // Aggregate existing data
                existingReport.grossRevenue += report.grossRevenue ?? 0;
                existingReport.totalSearches += report.totalSearches ?? 0;
                existingReport.originalSearches += report.originalSearches ?? 0;
                // ... other aggregations ...
            } else {
                // Push new aggregated data
                aggregatedReportsByDate[dateKey].feedTagData[feedTag].push({
                    ...report, // Add the missing properties from the 'ReportRecord' type
                    date: report.date,
                    originalSearches: report.originalSearches ?? 0,
                    totalSearches: report.totalSearches ?? 0,
                    grossRevenue: report.grossRevenue ?? 0,
                    // ... other report fields ...
                });
            }
        });

        // Process traffic reports
        trafficReports.forEach((trafficReport: TrafficReportRecord) => {
            const dateKey = moment.utc(trafficReport.date).format('DD-MM-YYYY');
            const feedTag = trafficReport.feedTag;

            if (!aggregatedReportsByDate[dateKey]) {
                aggregatedReportsByDate[dateKey] = { feedTagData: {} };
            }

            if (!aggregatedReportsByDate[dateKey].feedTagData[feedTag]) {
                aggregatedReportsByDate[dateKey].feedTagData[feedTag] = [];
            }

            const existingTrafficReport = aggregatedReportsByDate[dateKey].feedTagData[feedTag].find(
                (r) => moment.utc(r.date).format('DD-MM-YYYY') === dateKey
            );

            if (existingTrafficReport) {
                // Aggregate existing data
                existingTrafficReport.originalSearches = trafficReport.visits ?? 0;
                // ... other aggregations for traffic reports ...
            } else {
                // Push new aggregated data or default data if not present
                aggregatedReportsByDate[dateKey].feedTagData[feedTag].push({
                    date: trafficReport.date,
                    originalSearches: trafficReport.visits ?? 0,
                    grossRevenue: 0, // Default to 0 if no revenue data
                    offerId: trafficReport.offerId, // Add the missing properties from the 'ReportRecord' type
                    campaignId: trafficReport.campaignId,
                    trafficSourceId: trafficReport.trafficSourceId,
                    channelTag: trafficReport.channelTag,
                    feedTag: trafficReport.feedTag,
                    geo: '',
                    totalSearches: 0,
                    monetizedSearches: 0,
                    clicks: 0, // Add the missing properties from the 'ReportRecord' type
                    revenue: 0, // Add the missing properties from the 'ReportRecord' type
                    published: false, // Add the missing properties from the 'ReportRecord' type
                });
            }
        });

        // Transform the object into an array format and sort by date
        const sortedAggregatedReports = Object.keys(aggregatedReportsByDate)
            .map(date => ({
                date: date,
                feedTagData: aggregatedReportsByDate[date].feedTagData
            }))
            .sort((a, b) => moment.utc(a.date, 'DD-MM-YYYY').diff(moment.utc(b.date, 'DD-MM-YYYY')));

        return sortedAggregatedReports;
    }
);

export interface ReportStats {
    rpm: number;
    totalSearches: number;
    totalRevenue: number;
}

export const selectPerChannelStats = createSelector(
    [
        (state: RootState, campaignId: string, additionalProps?: Record<string, any>) => reportSelectByQuery(state.DBReportReducer, { campaignId, ...additionalProps }),
    ],
    (reports): Record<string, ReportStats> => {
        const statsPerChannel: Record<string, ReportStats> = {};
        const channelSearches: Record<string, number> = {};
        const channelRevenue: Record<string, number> = {};

        reports.forEach((report: ReportRecord) => {
            const channelTag = report.channelTag.toString();

            if (!channelSearches[channelTag]) {
                channelSearches[channelTag] = 0;
            }

            if (!channelRevenue[channelTag]) {
                channelRevenue[channelTag] = 0;
            }

            channelSearches[channelTag] += report.totalSearches ?? 0;
            channelRevenue[channelTag] += report.grossRevenue ?? 0;
        });

        Object.keys(channelSearches).forEach(channelTag => {
            const totalSearches = channelSearches[channelTag];
            const totalRevenue = channelRevenue[channelTag];
            const rpm = totalSearches > 0 ? (totalRevenue / totalSearches) * 1000 : 0;

            statsPerChannel[channelTag] = {
                rpm,
                totalSearches,
                totalRevenue,
            };
        });

        return statsPerChannel;
    }
);

// Select campaign Map which maps campaigns from trackerData by campaign id
export const selectCampaignMap = createSelector(
    [selectUnifiedCampaignList],
    (campaignList): Record<string, Campaign> => {
        const campaignMap: Record<string, Campaign> = {};

        campaignList?.forEach((campaign) => {
            campaignMap[campaign.id] = campaign;
        });

        return campaignMap;
    }
);

// Select affiliate network map
export const selectAffiliateNetworkMap = createSelector(
    [selectUnifiedAffiliateNetworkList],
    (affiliateNetworkList): Record<string, AffiliateNetwork> => {
        const affiliateNetworkMap: Record<string, AffiliateNetwork> = {};

        affiliateNetworkList?.forEach((affiliateNetwork) => {
            affiliateNetworkMap[affiliateNetwork.id] = affiliateNetwork;
        });

        return affiliateNetworkMap;
    }
);

export interface CampaignStats {
    campaignId: string;
    campaign: Campaign;
    totalGrossRevenue: number;
    totalRevenue: number;
    fraudRevenue: number;
    receivedPayments: number;
    totalPaid: number;

    channels: ChannelStats[];
}

export interface ChannelStats {
    channelTag: string;
    totalRevenue: number;
    fraudRevenue: number;
    receivedPayments: number;
    affiliateNetworkList: AffiliateNetwork[];
}


export interface ChannelAggregator {
    records: ReportRecord[];
    channelTag: string;
    affiliateNetworkList: AffiliateNetwork[];
}
export interface CampaignAggregator {
    campaignId: string;
    campaign: Campaign;
    channels: Record<string, ChannelAggregator>
}


// Select campaign stats aggregating revenue by campaignId and channelTag
export const selectCampaignStats = createSelector(
    [
        (state: RootState) => selectCampaignMap(state),
        (state: RootState, query: any) => reportSelectByQuery(state.DBReportReducer, query),
        (state: RootState) => selectAffiliateNetworkMap(state),
        (state: RootState, query: any) => paymentsSelectByQuery(state.DBPaymentsReducer, query)
    ],
    (campaignMap, reports, affiliateNetworkMap, paymentList): CampaignStats[] => {
        // Organize data by campaign and channel
        const campaigns: Record<string, CampaignAggregator> = {};
        reports.filter(report => report.published).forEach((report: ReportRecord) => {
            if (!campaigns[report.campaignId]) {
                campaigns[report.campaignId] = {
                    campaignId: report.campaignId,
                    campaign: campaignMap[report.campaignId],
                    channels: {}
                };
            }

            const campaign = campaigns[report.campaignId];

            if (!campaign.channels[report.channelTag]) {
                campaign.channels[report.channelTag] = {
                    channelTag: report.channelTag,
                    records: [],
                    affiliateNetworkList: [affiliateNetworkMap[report.affiliateNetworkId ?? '']],
                };
            } else {
                if (!campaign.channels[report.channelTag].affiliateNetworkList.find((an) => an?.id === report.affiliateNetworkId)) {
                    campaign.channels[report.channelTag].affiliateNetworkList.push(affiliateNetworkMap[report.affiliateNetworkId ?? '']);
                }
            }

            campaign.channels[report.channelTag].records.push(report);
        });

        

        // Aggregate data by channel
        Object.values(campaigns).forEach((campaign: any) => {
            Object.values(campaign.channels).forEach((channel: any) => {
                const channelStats = channel.records.reduce((acc: any, record: ReportRecord) => {
                    if (record.fraud) {
                        acc.fraudRevenue += record.revenue ?? 0;
                    } else if (record.published) {
                        acc.totalRevenue += record.revenue ?? 0;
                        if (record.received) {
                            acc.receivedPayments += record.received ? (record.revenue * (record.paidRatio ?? 1)) : 0;
                        }
                    }
                    return acc;
                }, { totalRevenue: 0, fraudRevenue: 0, receivedPayments: 0 });

                channel.totalRevenue = channelStats.totalRevenue;
                channel.fraudRevenue = channelStats.fraudRevenue;
                channel.receivedPayments = channelStats.receivedPayments;
            });
        });

        // Prepare final campaign stats
        const campaignStats: CampaignStats[] = Object.values(campaigns).map((campaign: any) => ({
            ...campaign,
            totalGrossRevenue: 0,
            totalRevenue: 0,
            fraudRevenue: 0,
            receivedPayments: 0,
            channels: Object.values(campaign.channels)
        }));

        // Aggregate campaign stats and sort
        campaignStats.forEach(campaign => {
            campaign.totalRevenue = Math.round(100 * campaign.channels.reduce((acc: number, ch: ChannelStats) => acc + ch.totalRevenue, 0)) / 100;
            campaign.fraudRevenue = Math.round(100 * campaign.channels.reduce((acc: number, ch: ChannelStats) => acc + ch.fraudRevenue, 0)) / 100;
            campaign.receivedPayments = Math.round(100 * campaign.channels.reduce((acc: number, ch: ChannelStats) => acc + ch.receivedPayments, 0)) / 100;
            campaign.totalPaid = Math.round(100 * paymentList.filter((payment) => payment.campaignId === campaign.campaignId).reduce((acc: number, payment) => acc + payment.amount, 0)) / 100;
        });

        campaignStats.sort((a, b) => b.totalRevenue - a.totalRevenue);

        return campaignStats;
    }
);


export interface AffiliateNetworkReceivable {
    affiliateNetworkId: string;
    feedTag: string;
    grossRevenue: number;
    receivedRevenue: number;
}

export const selectAffiliateNetworkReceivables = createSelector(
    [
        (state: RootState, query: any) => reportSelectByQuery(state.DBReportReducer, query),
    ],
    (reports): AffiliateNetworkReceivable[] => {
        const receivablesMap: { [key: string]: AffiliateNetworkReceivable } = {};

        reports.filter(report => report.published).forEach((report: ReportRecord) => {
            const key = `${report.affiliateNetworkId}_${report.feedTag}`;
            if (!receivablesMap[key]) {
                receivablesMap[key] = {
                    affiliateNetworkId: report.affiliateNetworkId ?? '',
                    feedTag: report.feedTag,
                    grossRevenue: 0,
                    receivedRevenue: 0,
                };
            }
            receivablesMap[key].grossRevenue += report.grossRevenue || 0;
            receivablesMap[key].receivedRevenue += report.received ? (report.grossRevenue * (report.paidRatio ?? 1)) || 0 : 0;
        });

        return Object.values(receivablesMap);
    }
);

export interface AffiliateNetworkSummary {
    affiliateNetworkId: string;
    totalGrossRevenue: number;
    totalReceivedRevenue: number;
    feeds: AffiliateNetworkReceivable[];
}

export const selectAffiliateNetworkSummaries = createSelector(
    [
        selectAffiliateNetworkReceivables,
    ],
    (receivables): AffiliateNetworkSummary[] => {
        const summariesMap: { [key: string]: AffiliateNetworkSummary } = {};

        receivables.forEach((receivable) => {
            if (!summariesMap[receivable.affiliateNetworkId]) {
                summariesMap[receivable.affiliateNetworkId] = {
                    affiliateNetworkId: receivable.affiliateNetworkId,
                    totalGrossRevenue: 0,
                    totalReceivedRevenue: 0,
                    feeds: []
                };
            }
            summariesMap[receivable.affiliateNetworkId].totalGrossRevenue += receivable.grossRevenue;
            summariesMap[receivable.affiliateNetworkId].totalReceivedRevenue += receivable.receivedRevenue;
            summariesMap[receivable.affiliateNetworkId].feeds.push(receivable);
        });

        return Object.values(summariesMap);
    }
);

interface MonthlyRevenue {
    year: number;
    month: number;
    grossRevenue: number;
    receivedRevenue: number;
}

export interface AffiliateNetworkMonthlyData {
    affiliateNetworkId: string;
    monthlyData: MonthlyRevenue[];
}

export const selectAffiliateNetworkMonthlyData = createSelector(
    [
        (state: RootState, query: any) => reportSelectByQuery(state.DBReportReducer, query),
    ],
    (reports): AffiliateNetworkMonthlyData[] => {
        const networkMonthlyDataMap: { [key: string]: AffiliateNetworkMonthlyData } ={};

        reports.filter(report => report.published).forEach((report: ReportRecord) => {
            const affiliateNetworkId = report.affiliateNetworkId ?? '';
            if (!networkMonthlyDataMap[affiliateNetworkId]) {
                networkMonthlyDataMap[affiliateNetworkId] = {
                    affiliateNetworkId,
                    monthlyData: []
                };
            }

            const reportDate = new Date(report.date);
            const year = reportDate.getFullYear();
            const month = reportDate.getMonth();

            let monthlyData = networkMonthlyDataMap[affiliateNetworkId].monthlyData.find(
                data => data.year === year && data.month === month
            );

            if (!monthlyData) {
                monthlyData = { year, month, grossRevenue: 0, receivedRevenue: 0 };
                networkMonthlyDataMap[affiliateNetworkId].monthlyData.push(monthlyData);
            }

            monthlyData.grossRevenue += report.grossRevenue || 0;
            monthlyData.receivedRevenue += report.received ? (report.grossRevenue * (report.paidRatio ?? 1)) || 0 : 0;
        });

        // Sort monthly data for each network
        Object.values(networkMonthlyDataMap).forEach(networkData => {
            networkData.monthlyData.sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                return a.month - b.month;
            });
        });

        return Object.values(networkMonthlyDataMap);
    }
);

export const selectAllMonthsAcrossNetworks = createSelector(
    [selectAffiliateNetworkMonthlyData],
    (networkMonthlyData): { year: number; month: number }[] => {
        const allMonths = new Set<string>();

        networkMonthlyData.forEach(network => {
            network.monthlyData.forEach(data => {
                allMonths.add(`${data.year}-${data.month}`);
            });
        });

        return Array.from(allMonths)
            .map(monthStr => {
                const [year, month] = monthStr.split('-').map(Number);
                return { year, month };
            })
            .sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                return a.month - b.month;
            });
    }
);